import { type FC } from "react";

import { Button, UnstyledButton } from "@mantine/core";
import clsx from "clsx";

import { myLensesActionType } from "../../analytics/event-creators/createActionEvent";
import { useBlizzardEvents } from "../../analytics/hooks";
import { ClosableModal } from "../../shared/components";
import { type ModalProps } from "../../shared/hooks";
import { LENS_PLUS_LEARN_MORE_LINK_URL } from "../constants";

import {
    cardBody,
    cardExample,
    cardNumber,
    cardNumberEmphasis,
    cardTitle,
    cardsRow,
    eyebrow,
    footer,
    gotItButton,
    guidelinesIcon,
    guidelinesLink,
    infoCard,
    infoCardEmphasis,
    intro,
    modal,
    tipCallout,
    tipIcon,
    tipLabel,
    tipText,
    title,
} from "./HowPayoutsWorkModal.module.css";
import {
    HowPayoutsWorkAudienceBody,
    HowPayoutsWorkAudienceExample,
    HowPayoutsWorkAudienceTitle,
    HowPayoutsWorkEyebrow,
    HowPayoutsWorkGotIt,
    HowPayoutsWorkModalIntro,
    HowPayoutsWorkModalTitle,
    HowPayoutsWorkReadFullGuidelines,
    HowPayoutsWorkScoreBody,
    HowPayoutsWorkScoreExample,
    HowPayoutsWorkScoreTitle,
    HowPayoutsWorkSubscriptionsBody,
    HowPayoutsWorkSubscriptionsExample,
    HowPayoutsWorkSubscriptionsTitle,
    HowPayoutsWorkTipBody,
    HowPayoutsWorkTipLabel,
} from "./messages.generated";

interface HowPayoutsWorkModalProps {
    modalProps: ModalProps;
}

const MODAL_MAX_WIDTH = 1100;

export const HowPayoutsWorkModal: FC<HowPayoutsWorkModalProps> = ({ modalProps }) => {
    const { sendBlizzardEvent } = useBlizzardEvents();

    const handleGotIt = (): void => {
        sendBlizzardEvent({
            event: "MYLENSES_ACTION",
            actionType: myLensesActionType.click,
            label: "payout_drivers_got_it_click",
            namespace: "lenses",
        });
        modalProps.onCancel();
    };

    const handleReadGuidelines = (): void => {
        sendBlizzardEvent({
            event: "MYLENSES_ACTION",
            actionType: myLensesActionType.click,
            label: "payout_drivers_read_guidelines_click",
            namespace: "lenses",
        });
    };

    return (
        <ClosableModal modalProps={modalProps} width={MODAL_MAX_WIDTH} className={modal}>
            <div className={eyebrow}>
                <HowPayoutsWorkEyebrow />
            </div>
            <h2 className={title}>
                <HowPayoutsWorkModalTitle />
            </h2>
            <p className={intro}>
                <HowPayoutsWorkModalIntro />
            </p>
            <div className={cardsRow}>
                <div className={infoCard}>
                    <div className={cardNumber}>1</div>
                    <div className={cardTitle}>
                        <HowPayoutsWorkAudienceTitle />
                    </div>
                    <div className={cardBody}>
                        <HowPayoutsWorkAudienceBody />
                    </div>
                    <div className={cardExample}>
                        <HowPayoutsWorkAudienceExample />
                    </div>
                </div>
                <div className={infoCard}>
                    <div className={cardNumber}>2</div>
                    <div className={cardTitle}>
                        <HowPayoutsWorkSubscriptionsTitle />
                    </div>
                    <div className={cardBody}>
                        <HowPayoutsWorkSubscriptionsBody />
                    </div>
                    <div className={cardExample}>
                        <HowPayoutsWorkSubscriptionsExample />
                    </div>
                </div>
                <div className={clsx(infoCard, infoCardEmphasis)}>
                    <div className={clsx(cardNumber, cardNumberEmphasis)}>3</div>
                    <div className={cardTitle}>
                        <HowPayoutsWorkScoreTitle />
                    </div>
                    <div className={cardBody}>
                        <HowPayoutsWorkScoreBody />
                    </div>
                    <div className={cardExample}>
                        <HowPayoutsWorkScoreExample />
                    </div>
                </div>
            </div>
            <div className={tipCallout}>
                <span className={tipIcon} aria-hidden="true">
                    💡
                </span>
                <span className={tipText}>
                    <span className={tipLabel}>
                        <HowPayoutsWorkTipLabel />
                    </span>{" "}
                    <HowPayoutsWorkTipBody />
                </span>
            </div>
            <div className={footer}>
                <UnstyledButton
                    component="a"
                    href={LENS_PLUS_LEARN_MORE_LINK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleReadGuidelines}
                    className={guidelinesLink}
                >
                    <span className={guidelinesIcon} aria-hidden="true">
                        📖
                    </span>
                    <HowPayoutsWorkReadFullGuidelines />
                </UnstyledButton>
                <Button onClick={handleGotIt} className={gotItButton}>
                    <HowPayoutsWorkGotIt />
                </Button>
            </div>
        </ClosableModal>
    );
};
